import { Link } from "react-router-dom";
import { CheckCircle2, Calendar, Wallet, ExternalLink, Phone, MessageCircle, GraduationCap, FileText, Info } from "lucide-react";
import { PageHero } from "@/components/site/PageHero";
import { Reveal, SectionHeading } from "@/components/site/primitives";
import { ApplicationProcess } from "@/components/site/ApplicationProcess";
import { LocationSection } from "@/components/home/LocationSection";
import { ApplyButton } from "@/components/site/AdmissionBadge";
import { ELIGIBILITY, DISCLAIMERS, STATUS_META, IMAGES } from "@/data/content";
import { useSiteConfig } from "@/context/SiteConfigContext";
import { telHref, waHref } from "@/lib/links";
import { useSEO } from "@/hooks/useSite";

const REQUIREMENTS = [
  "Valid SSCE / O'Level result (or awaiting result)",
  "Completed application / enquiry form",
  "Passport photograph",
  "Chosen subject combination aligned to your intended course",
  "Any additional documents requested for your session",
];

export default function Admissions() {
  const { config } = useSiteConfig();
  const meta = STATUS_META[config.admissionStatus] || STATUS_META.OPEN;
  const closed = config.admissionStatus === "CLOSED";
  useSEO({
    title: "Admissions | IAUE School of Basic Studies — JUPEB & IJMB",
    description: "Current admission status, eligibility, requirements, fees, deadlines and application process for JUPEB & IJMB at the School of Basic Studies, IAUE Port Harcourt.",
  });

  return (
    <>
      <PageHero overline="Admissions" title="Admissions" crumb="Admissions"
        subtitle="Everything you need to know to apply this session — kept up to date by the School." image={IMAGES.graduation} />

      {/* Current status */}
      <section className="py-16 bg-white">
        <div className="container-x">
          <div className="rounded-sm border border-slate-200 overflow-hidden">
            <div className="grid md:grid-cols-3">
              <div className="p-8 text-white md:col-span-1" style={{ background: closed ? "var(--red)" : "linear-gradient(135deg,#0A1E4E,#16347A)" }} data-testid="admission-status-card">
                <span className="overline text-white/70">Current Admission Status</span>
                <div className="mt-4 flex items-center gap-3">
                  <span className="relative flex h-3.5 w-3.5">
                    <span className="pulse-dot inline-flex h-3.5 w-3.5 rounded-full" style={{ background: meta.dot }} />
                  </span>
                  <span className="font-serif text-2xl font-bold">{meta.label}</span>
                </div>
                <p className="mt-4 text-blue-100/85">{config.bannerText}</p>
              </div>
              <div className="p-8 md:col-span-2 grid sm:grid-cols-2 gap-6">
                <div className="flex items-start gap-3">
                  <GraduationCap className="text-[#D4AF37]" />
                  <div><div className="text-xs uppercase tracking-wide text-slate-500">Academic Session</div>
                    <div className="font-serif text-xl font-bold text-[#0A1E4E]" data-testid="admissions-session">{config.academicSession}</div></div>
                </div>
                <div className="flex items-start gap-3">
                  <Calendar className="text-[#D4AF37]" />
                  <div><div className="text-xs uppercase tracking-wide text-slate-500">Application Deadline</div>
                    <div className="font-serif text-xl font-bold text-[#0A1E4E]" data-testid="admissions-deadline">{config.deadline ? config.deadline : "Apply while admissions remain open"}</div></div>
                </div>
                <div className="sm:col-span-2 flex flex-wrap gap-3 pt-2">
                  <ApplyButton className="btn-red" testId="admissions-apply-button">Apply Now</ApplyButton>
                  <a href={telHref(config.phonePrimary)} className="btn-navy"><Phone size={15} /> {config.phonePrimary}</a>
                  <a href={waHref(config.whatsapp)} target="_blank" rel="noopener noreferrer" className="btn-outline-gold !text-[#0A1E4E] !border-[#0A1E4E] hover:!bg-[#0A1E4E] hover:!text-white"><MessageCircle size={15} /> WhatsApp</a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Programmes + eligibility + requirements */}
      <section className="py-8 bg-white">
        <div className="container-x grid lg:grid-cols-2 gap-8">
          <Reveal>
            <div className="h-full rounded-sm border border-slate-200 p-8">
              <SectionHeading overline="Available Programmes" title="Choose Your Pathway" />
              <div className="mt-6 grid grid-cols-3 gap-4">
                {[["JUPEB", "/jupeb"], ["IJMB", "/ijmb"], ["Remedial", "/remedial"]].map(([p, to]) => (
                  <Link key={p} to={to} className="group rounded-sm border-2 border-slate-200 p-4 sm:p-6 text-center transition-all hover:border-[#D4AF37] hover:bg-[#F7F5EF]" data-testid={`admissions-programme-${p.toLowerCase()}`}>
                    <div className="font-serif text-lg sm:text-2xl font-bold text-[#0A1E4E]">{p}</div>
                    <div className="mt-1 text-xs sm:text-sm text-slate-500">{p === "Remedial" ? "Foundation programme" : "Advanced-level programme"}</div>
                  </Link>
                ))}
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.08}>
            <div className="h-full rounded-sm border border-slate-200 p-8">
              <SectionHeading overline="Requirements" title="What You'll Need" />
              <ul className="mt-6 space-y-3">
                {REQUIREMENTS.map((r) => (
                  <li key={r} className="flex items-start gap-3 text-slate-700"><FileText size={18} className="mt-0.5 shrink-0 text-[#D4AF37]" /> {r}</li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-12 bg-white">
        <div className="container-x">
          <div className="rounded-sm border border-slate-200 p-8">
            <SectionHeading overline="Eligibility" title="Who Can Apply?" />
            <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {ELIGIBILITY.map((e) => (
                <div key={e} className="flex items-start gap-2.5 text-slate-800"><CheckCircle2 size={20} className="mt-0.5 shrink-0 text-[#C0202A]" /> {e}</div>
              ))}
            </div>
            <p className="mt-5 flex items-center gap-2 text-sm text-slate-500"><Info size={15} /> {DISCLAIMERS.eligibility}</p>
          </div>
        </div>
      </section>

      {/* Fees + application link */}
      <section className="py-12 bg-white">
        <div className="container-x grid lg:grid-cols-2 gap-8">
          <Reveal>
            <div className="h-full rounded-sm border border-slate-200 p-8" data-testid="fees-card">
              <div className="flex items-center gap-3"><Wallet className="text-[#D4AF37]" /><h3 className="font-serif text-2xl font-bold text-[#0A1E4E]">Fees</h3></div>
              <div className="mt-6 space-y-4">
                <div className="rounded-sm bg-[#F7F5EF] p-5"><div className="font-bold text-[#0A1E4E]">JUPEB</div><p className="mt-1 text-slate-600">{config.feesJupeb}</p></div>
                <div className="rounded-sm bg-[#F7F5EF] p-5"><div className="font-bold text-[#0A1E4E]">IJMB</div><p className="mt-1 text-slate-600">{config.feesIjmb}</p></div>
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.08}>
            <div className="h-full rounded-sm bg-[#0A1E4E] p-8 text-white flex flex-col justify-center">
              <span className="overline text-[#D4AF37]">Application Link</span>
              <h3 className="mt-3 font-serif text-2xl font-bold">Ready to Apply?</h3>
              <p className="mt-2 text-blue-100/85">
                {config.applicationUrl ? "Complete your application through the official application portal." : "Start your application by submitting an enquiry — an admissions officer will guide you through the process."}
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <ApplyButton className="btn-red" testId="admissions-cta-apply">
                  {config.applicationUrl ? <>Open Application Portal <ExternalLink size={15} /></> : "Start Application"}
                </ApplyButton>
                <a href={waHref(config.whatsapp)} target="_blank" rel="noopener noreferrer" className="btn-gold"><MessageCircle size={15} /> Chat on WhatsApp</a>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <ApplicationProcess light={false} />
      <LocationSection />
    </>
  );
}
